import type {
  ExperimentProgressEntry,
  ExperimentProgressMetric,
  ExperimentProgressStatus,
  ExperimentProjection,
  RunProjection
} from "@roboscientist/contracts";

export const progressStatusLabel: Record<ExperimentProgressStatus, string> = {
  running: "In progress",
  planned: "Planned next",
  completed: "Completed",
  blocked: "Blocked",
  cancelled: "Cancelled"
};

export const progressStatusTone = (status: ExperimentProgressStatus) => {
  if (status === "running") return "live" as const;
  if (status === "completed") return "accent" as const;
  if (status === "blocked") return "danger" as const;
  if (status === "cancelled") return "warning" as const;
  return "neutral" as const;
};

export const completedProgressColumns = (
  width: number,
  cardMinWidth = 280,
  gap = 7
): number => Math.max(1, Math.floor((Math.max(0, width) + gap) / (cardMinWidth + gap)));

export type ExperimentProgressRunContext = {
  scope: "run" | "experiment";
  status: ExperimentProgressStatus | null;
  title: string;
  text: string;
  metric: ExperimentProgressMetric | null;
  entryId: string | null;
};

const normalizedPath = (value: string): string => value
  .replaceAll("\\", "/")
  .replace(/\/{2,}/g, "/")
  .replace(/^\.\//, "")
  .replace(/\/$/, "");

const relativeRunRoot = (experimentRoot: string, runPath: string): string | null => {
  const root = normalizedPath(experimentRoot);
  const candidate = normalizedPath(runPath);
  if (candidate === root) return "";
  return candidate.startsWith(root + "/") ? candidate.slice(root.length + 1) : null;
};

const referencesRun = (entry: ExperimentProgressEntry, runRoot: string): boolean => (
  Boolean(runRoot) && entry.references.some((reference) => {
    const normalized = normalizedPath(reference);
    return normalized === runRoot || normalized.startsWith(runRoot + "/");
  })
);

export function progressContextForRun(
  experiment: Pick<ExperimentProjection, "id" | "rootPath" | "progress">,
  run: Pick<RunProjection, "experimentId" | "path">
): ExperimentProgressRunContext | null {
  const progress = experiment.progress;
  if (!progress || experiment.id !== run.experimentId) return null;
  const runRoot = relativeRunRoot(experiment.rootPath, run.path);
  const matching = runRoot === null
    ? []
    : progress.progress.filter((entry) => referencesRun(entry, runRoot));
  const entry = matching.reduce<ExperimentProgressEntry | null>((latest, candidate) => {
    if (!latest) return candidate;
    return Date.parse(candidate.updatedAt) > Date.parse(latest.updatedAt) ? candidate : latest;
  }, null);
  if (entry) return {
    scope: "run",
    status: entry.status,
    title: entry.title,
    text: entry.result ?? entry.description,
    metric: entry.metrics[0] ?? null,
    entryId: entry.id
  };
  return {
    scope: "experiment",
    status: null,
    title: progress.title,
    text: progress.summary,
    metric: null,
    entryId: null
  };
}
